import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { GeoService, MAP_MAX_ZOOM, MAP_MIN_ZOOM, TILE_SIZE } from '@app/shared/geo.service';

interface MapTile { url: string; left: number; top: number; key: string; }

// Mini-carte OSM SANS dépendance npm, même technique que DelrunsMapComponent (logistic) :
// mosaïque de tuiles statiques (config GeoService) + projection Web Mercator (GeoService).
// Un seul marqueur. Interactive : pan (glisser la carte), déplacement du marqueur (drag => coordsChange),
// zoom boutons ± / Ctrl+molette / double-clic, recentrage. Le parent reste maître des coordonnées via [lat]/[lon].
@Component({
	selector: 'app-mini-map',
	imports: [MatButtonModule, MatIconModule],
	templateUrl: './mini-map.component.html',
	styleUrl: './mini-map.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true,
})
export class MiniMapComponent implements OnChanges {

	@Input() lat: number | null = null;
	@Input() lon: number | null = null;
	@Input() label = '';
	@Input() height = 220;
	@Output() coordsChange = new EventEmitter<{ lat: number; lon: number }>();	// marqueur déplacé à la main

	private host: ElementRef<HTMLElement> = inject(ElementRef);

	public tiles: MapTile[] = [];
	public zoom = 16;			// niveau rue
	public markerLeft = 0;
	public markerTop = 0;
	public centerLat = 0;	// centre de la vue, découplé du marqueur : le pan ne déplace pas le pin
	public centerLon = 0;
	private mLat = 0;			// position géo du marqueur
	private mLon = 0;
	private left0 = 0;		// origine px « monde » du viewport, posée par render()
	private top0 = 0;
	// baseA/baseB : marker => px départ du pin ; pan => px « monde » du centre au départ
	public drag: { mode: 'pan' | 'marker'; startX: number; startY: number; baseA: number; baseB: number } | null = null;

	get hasPoint(): boolean {
		return this.lat !== null && this.lat !== undefined && this.lon !== null && this.lon !== undefined
			&& !isNaN(+this.lat) && !isNaN(+this.lon);
	}

	ngOnChanges(changes?: SimpleChanges): void {
		if (!this.hasPoint) { this.tiles = []; return; }
		// changement issu de notre propre coordsChange (drag du pin) : garder la vue en place
		if (this.tiles.length && +this.lat! === this.mLat && +this.lon! === this.mLon) {
			if (changes?.['height']) this.render();		// seule la hauteur a changé : re-rendu du viewport
			return;
		}
		this.mLat = +this.lat!;
		this.mLon = +this.lon!;
		this.centerLat = this.mLat;
		this.centerLon = this.mLon;
		this.render();
	}

	zoomBy(dz: number): void {
		this.zoom = Math.min(MAP_MAX_ZOOM, Math.max(MAP_MIN_ZOOM, this.zoom + dz));
		this.render();
	}

	recenter(): void {		// recadre la vue sur le marqueur (utile après un pan lointain)
		this.centerLat = this.mLat;
		this.centerLon = this.mLon;
		this.render();
	}

	// Ctrl + molette = zoom (cohérent avec la carte des tournées) ; molette seule = scroll normal de la
	// page — important sur la fiche client, qui peut empiler plusieurs mini-cartes sur le chemin du scroll.
	onWheel(ev: WheelEvent): void {
		if (!ev.ctrlKey) return;
		ev.preventDefault();
		this.zoomBy(ev.deltaY < 0 ? 1 : -1);
	}

	// Double-clic = zoom avant (ignoré sur les boutons zoom/recentrage)
	onDblClick(ev: MouseEvent): void {
		if ((ev.target as HTMLElement)?.closest?.('button')) return;
		ev.preventDefault();
		this.zoomBy(1);
	}

	// --- Drag : pan de la carte ou déplacement du marqueur (Pointer Events : souris + tactile) ---

	onMarkerDown(ev: PointerEvent): void {
		ev.stopPropagation();		// ne pas déclencher le pan
		ev.preventDefault();
		this.capture(ev);
		this.drag = { mode: 'marker', startX: ev.clientX, startY: ev.clientY, baseA: this.markerLeft, baseB: this.markerTop };
	}

	onPanDown(ev: PointerEvent): void {
		if ((ev.target as HTMLElement)?.closest?.('button')) return;		// laisser vivre les boutons zoom/recentrage
		ev.preventDefault();
		this.capture(ev);
		this.drag = {
			mode: 'pan', startX: ev.clientX, startY: ev.clientY,
			baseA: GeoService.lonToX(this.centerLon, this.zoom),
			baseB: GeoService.latToY(this.centerLat, this.zoom),
		};
	}

	onMove(ev: PointerEvent): void {
		if (!this.drag) return;
		const dx = ev.clientX - this.drag.startX, dy = ev.clientY - this.drag.startY;
		if (this.drag.mode === 'marker') {
			this.markerLeft = this.drag.baseA + dx;		// le pin suit le curseur, conversion géo au lâcher
			this.markerTop = this.drag.baseB + dy;
		} else {
			this.centerLon = GeoService.xToLon(this.drag.baseA - dx, this.zoom);
			this.centerLat = GeoService.yToLat(this.drag.baseB - dy, this.zoom);
			this.render();
		}
	}

	onUp(_ev: PointerEvent): void {
		if (!this.drag) return;
		if (this.drag.mode === 'marker') {
			this.mLon = +GeoService.xToLon(this.left0 + this.markerLeft, this.zoom).toFixed(6);	// 6 déc. ≈ 10 cm
			this.mLat = +GeoService.yToLat(this.top0 + this.markerTop, this.zoom).toFixed(6);
			this.coordsChange.emit({ lat: this.mLat, lon: this.mLon });
			this.render();
		}
		this.drag = null;
	}

	private capture(ev: PointerEvent): void {
		try { (ev.target as Element)?.setPointerCapture?.(ev.pointerId); } catch { /* runner de test sans pointeur réel */ }
	}

	private frameWidth(): number {
		return this.host?.nativeElement?.clientWidth || 480;
	}

	// (Re)calcule la mosaïque de tuiles pour la vue courante et la position px du marqueur
	render(): void {
		if (!this.hasPoint) return;
		const W = this.frameWidth(), H = this.height, z = this.zoom;
		const n = Math.pow(2, z);
		this.left0 = GeoService.lonToX(this.centerLon, z) - W / 2;
		this.top0 = GeoService.latToY(this.centerLat, z) - H / 2;

		const tiles: MapTile[] = [];
		for (let tx = Math.floor(this.left0 / TILE_SIZE); tx <= Math.floor((this.left0 + W) / TILE_SIZE); tx++) {
			for (let ty = Math.floor(this.top0 / TILE_SIZE); ty <= Math.floor((this.top0 + H) / TILE_SIZE); ty++) {
				if (ty < 0 || ty >= n) continue;							// pas de tuile au-delà des pôles
				const wrappedX = ((tx % n) + n) % n;					// wrap de la longitude
				tiles.push({
					url: GeoService.tileUrlOf(z, wrappedX, ty),
					left: tx * TILE_SIZE - this.left0,
					top: ty * TILE_SIZE - this.top0,
					key: `${z}/${tx}/${ty}`,
				});
			}
		}
		this.tiles = tiles;
		this.markerLeft = GeoService.lonToX(this.mLon, z) - this.left0;
		this.markerTop = GeoService.latToY(this.mLat, z) - this.top0;
	}
}
